import React, { useCallback, useEffect, useState } from 'react';
import type { LocalEmployee } from '../../services/localEmployeesStore';
import MyEmployeesSection from './MyEmployeesSection';
import EmployeeGradingSection, {
  type GradingSavedHighlight,
} from './employeeGrading/EmployeeGradingSection';
import type { WorkspaceTourPrepare } from './tour/workspaceTourConfig';
import './EmployeesSection.css';

type EmployeesTab = 'list' | 'grading';

type EmployeesSectionProps = {
  tourPrepareKind?: WorkspaceTourPrepare | null;
  tourPrepareNonce?: number;
};

export default function EmployeesSection({
  tourPrepareKind,
  tourPrepareNonce = 0,
}: EmployeesSectionProps) {
  const [tab, setTab] = useState<EmployeesTab>('list');
  const [gradeEmployeeIntent, setGradeEmployeeIntent] = useState<LocalEmployee | null>(null);
  const [gradePositionIntent, setGradePositionIntent] = useState<string | null>(null);
  const [gradingSavedToken, setGradingSavedToken] = useState(0);
  const [pendingHighlight, setPendingHighlight] = useState<GradingSavedHighlight | null>(null);

  useEffect(() => {
    if (tourPrepareKind === 'employees-list' && tourPrepareNonce > 0) {
      setTab('list');
    }
  }, [tourPrepareKind, tourPrepareNonce]);

  const handleGradeEmployee = (employee: LocalEmployee) => {
    setPendingHighlight(null);
    setGradePositionIntent(null);
    setGradeEmployeeIntent(employee);
    setTab('grading');
  };

  const handleGradePosition = (position: string) => {
    setPendingHighlight(null);
    setGradeEmployeeIntent(null);
    setGradePositionIntent(position);
    setTab('grading');
  };

  const handleGradeIntentHandled = useCallback(() => {
    setGradeEmployeeIntent(null);
    setGradePositionIntent(null);
  }, []);

  const handleGradingSaved = useCallback((highlight: GradingSavedHighlight) => {
    setPendingHighlight(highlight);
    setGradingSavedToken((value) => value + 1);
    setTab('list');
  }, []);

  const clearHighlight = useCallback(() => {
    setPendingHighlight(null);
  }, []);

  return (
    <div className="employees-section">
      <div className="employees-section-tabs" role="tablist" aria-label="Раздел сотрудников" data-tour="employees-tabs">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'list'}
          className={`employees-section-tab${tab === 'list' ? ' employees-section-tab-active' : ''}`}
          onClick={() => {
            clearHighlight();
            setTab('list');
          }}
        >
          <i className="fas fa-users" aria-hidden="true" />
          Мои сотрудники
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'grading'}
          className={`employees-section-tab${tab === 'grading' ? ' employees-section-tab-active' : ''}`}
          onClick={() => {
            clearHighlight();
            setTab('grading');
          }}
        >
          <i className="fas fa-layer-group" aria-hidden="true" />
          Грейдирование
        </button>
      </div>

      <div role="tabpanel">
        <div style={{ display: tab === 'list' ? 'block' : 'none' }}>
          <MyEmployeesSection
            embedded
            onGradeEmployee={handleGradeEmployee}
            onGradePosition={handleGradePosition}
            gradingSavedToken={gradingSavedToken}
            gradingSavedHighlight={pendingHighlight}
            onClearHighlight={clearHighlight}
          />
        </div>
        <div style={{ display: tab === 'grading' ? 'block' : 'none' }}>
          <EmployeeGradingSection
            gradeEmployeeIntent={gradeEmployeeIntent}
            gradePositionIntent={gradePositionIntent}
            onGradeIntentHandled={handleGradeIntentHandled}
            onGradingSaved={handleGradingSaved}
          />
        </div>
      </div>
    </div>
  );
}
